import React, { useState, useRef, useEffect } from "react";
import {
  Send,
  BarChart3,
  Table,
  FileText,
  Shield,
  User,
  Bot,
} from "lucide-react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

interface Message {
  id: string;
  type: "user" | "assistant";
  content: string;
  timestamp: Date;
  responseType?: "chart" | "table" | "narrative";
  data?: any;
}

interface TableData {
  headers: string[];
  rows: any[][];
}

const ConversationalInsightsCopilot: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([
    {
      id: "1",
      type: "assistant",
      content:
        "Hello! I'm your Conversational Insights assistant. Ask me anything about your operational data, such as response times, SLA compliance, or alarm patterns.",
      timestamp: new Date(),
    },
  ]);
  const [input, setInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Mock response generator
  const generateResponse = (query: string): Partial<Message> => {
    const lowerQuery = query.toLowerCase();

    if (
      lowerQuery.includes("response time") ||
      lowerQuery.includes("code blue")
    ) {
      return {
        content:
          "Here are the Code Blue response times for night shifts last week compared to the target SLA of 3 minutes:",
        responseType: "chart",
        data: [
          { day: "Mon", actual: 2.8, target: 3.0 },
          { day: "Tue", actual: 3.2, target: 3.0 },
          { day: "Wed", actual: 2.5, target: 3.0 },
          { day: "Thu", actual: 2.9, target: 3.0 },
          { day: "Fri", actual: 3.4, target: 3.0 },
          { day: "Sat", actual: 2.7, target: 3.0 },
          { day: "Sun", actual: 2.6, target: 3.0 },
        ],
      };
    } else if (
      lowerQuery.includes("alarm") ||
      lowerQuery.includes("notification")
    ) {
      return {
        content: "Alarm notification analysis for the past week:",
        responseType: "table",
        data: {
          headers: [
            "Unit",
            "Total Alarms",
            "Response Rate",
            "Avg Response Time",
          ],
          rows: [
            ["ICU-A", "234", "92%", "1.2 min"],
            ["ER", "456", "88%", "1.8 min"],
            ["ICU-B", "189", "95%", "0.9 min"],
            ["Med-Surg 3", "312", "79%", "2.4 min"],
            ["CCU", "267", "91%", "1.3 min"],
          ],
        } as TableData,
      };
    } else {
      return {
        content:
          "Based on the analysis of your operational data:\n\n• Average response time across all units: 1.6 minutes\n• SLA compliance rate: 87%\n• Peak alarm periods: 7-9 AM and 6-8 PM\n• Units with highest alarm fatigue risk: Med-Surg 3 and ER\n\nRecommendation: Consider implementing alarm bundling during peak hours and additional staff coverage for high-risk units.",
        responseType: "narrative",
      };
    }
  };

  const handleSend = () => {
    if (!input.trim()) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      type: "user",
      content: input,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setIsTyping(true);

    // Simulate AI response
    setTimeout(() => {
      const response = generateResponse(input);
      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        type: "assistant",
        timestamp: new Date(),
        ...(response as Message),
      };
      setMessages((prev) => [...prev, assistantMessage]);
      setIsTyping(false);
    }, 1500);
  };

  const renderResponse = (message: Message) => {
    if (message.responseType === "chart" && message.data) {
      return (
        <div className="mt-4 bg-white p-4 rounded-lg shadow-sm">
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={message.data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="day" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line
                type="monotone"
                dataKey="actual"
                stroke="#3b82f6"
                name="Actual Time (min)"
                strokeWidth={2}
              />
              <Line
                type="monotone"
                dataKey="target"
                stroke="#ef4444"
                name="Target SLA (min)"
                strokeDasharray="5 5"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      );
    } else if (message.responseType === "table" && message.data) {
      const tableData = message.data as TableData;
      return (
        <div className="mt-4 overflow-x-auto">
          <table className="min-w-full bg-white rounded-lg overflow-hidden shadow-sm">
            <thead className="bg-gray-50">
              <tr>
                {tableData.headers.map((header, idx) => (
                  <th
                    key={idx}
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {tableData.rows.map((row, rowIdx) => (
                <tr key={rowIdx} className="hover:bg-gray-50">
                  {row.map((cell, cellIdx) => (
                    <td
                      key={cellIdx}
                      className="px-4 py-3 text-sm text-gray-900"
                    >
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="flex flex-col h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="bg-blue-600 p-2 rounded-lg">
              <BarChart3 className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-semibold text-gray-900">
                Conversational Insights
              </h1>
              <p className="text-xs text-gray-500">
                Secure operational analytics
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2 text-green-600">
            <Shield className="w-4 h-4" />
            <span className="text-xs font-medium">Encrypted</span>
          </div>
        </div>
      </div>

      {/* Messages Container */}
      <div className="flex-1 overflow-y-auto px-4 py-4">
        <div className="max-w-4xl mx-auto space-y-4">
          {messages.map((message) => (
            <div
              key={message.id}
              className={`flex ${
                message.type === "user" ? "justify-end" : "justify-start"
              }`}
            >
              <div
                className={`flex space-x-3 max-w-3xl ${
                  message.type === "user"
                    ? "flex-row-reverse space-x-reverse"
                    : ""
                }`}
              >
                <div
                  className={`flex-shrink-0 ${
                    message.type === "user" ? "bg-blue-600" : "bg-gray-600"
                  } w-8 h-8 rounded-full flex items-center justify-center`}
                >
                  {message.type === "user" ? (
                    <User className="w-4 h-4 text-white" />
                  ) : (
                    <Bot className="w-4 h-4 text-white" />
                  )}
                </div>
                <div className="flex-1">
                  <div
                    className={`rounded-lg px-4 py-2 ${
                      message.type === "user"
                        ? "bg-blue-600 text-white"
                        : "bg-white shadow-sm border border-gray-200 text-gray-900"
                    }`}
                  >
                    <p className="text-sm whitespace-pre-line">
                      {message.content}
                    </p>
                  </div>
                  {message.type === "assistant" && renderResponse(message)}
                  <p className="text-xs text-gray-500 mt-1">
                    {message.timestamp.toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </p>
                </div>
              </div>
            </div>
          ))}

          {isTyping && (
            <div className="flex justify-start">
              <div className="flex space-x-3 max-w-3xl">
                <div className="flex-shrink-0 bg-gray-600 w-8 h-8 rounded-full flex items-center justify-center">
                  <Bot className="w-4 h-4 text-white" />
                </div>
                <div className="bg-white shadow-sm border border-gray-200 rounded-lg px-4 py-2">
                  <div className="flex space-x-1">
                    <div
                      className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                      style={{ animationDelay: "0ms" }}
                    ></div>
                    <div
                      className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                      style={{ animationDelay: "150ms" }}
                    ></div>
                    <div
                      className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                      style={{ animationDelay: "300ms" }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input Area */}
      <div className="bg-white border-t border-gray-200 px-4 py-4">
        <div className="max-w-4xl mx-auto">
          <div className="flex space-x-3">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={(e) => e.key === "Enter" && handleSend()}
              placeholder="Ask about response times, SLA compliance, alarm patterns..."
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            />
            <button
              onClick={handleSend}
              disabled={!input.trim()}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <button className="text-xs bg-gray-100 hover:bg-gray-200 px-3 py-1 rounded-full text-gray-700 transition-colors">
              <BarChart3 className="w-3 h-3 inline mr-1" />
              Response times
            </button>
            <button className="text-xs bg-gray-100 hover:bg-gray-200 px-3 py-1 rounded-full text-gray-700 transition-colors">
              <Table className="w-3 h-3 inline mr-1" />
              Alarm analysis
            </button>
            <button className="text-xs bg-gray-100 hover:bg-gray-200 px-3 py-1 rounded-full text-gray-700 transition-colors">
              <FileText className="w-3 h-3 inline mr-1" />
              SLA compliance
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConversationalInsightsCopilot;
